/**
 * Rough potential payout if the picked outcome wins (preview only).
 */

function foldOutcome(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Map stored prediction outcome to boost-stats bucket key (match: TeamA/Draw/TeamB). */
export function boostOutcomeStatsKey(outcome, item, isPoll = false) {
  const o = String(outcome || '').trim();
  if (!o) return o;
  if (isPoll) {
    if (item?.optionType === 'options' && Array.isArray(item?.options)) {
      const hit = item.options.find(
        (opt) => foldOutcome(opt?.text) === foldOutcome(o)
      );
      if (hit) return String(hit.text).trim();
    }
    const up = o.toUpperCase();
    if (up === 'YES' || up === 'NO') return up;
    return o;
  }
  const teamA = String(item?.teamA || '').trim();
  const teamB = String(item?.teamB || '').trim();
  const lower = foldOutcome(o);
  if (o === 'TeamA' || lower === 'teama' || (teamA && lower === foldOutcome(teamA))) return 'TeamA';
  if (o === 'TeamB' || lower === 'teamb' || (teamB && lower === foldOutcome(teamB))) return 'TeamB';
  if (o === 'Draw' || lower === 'draw') return 'Draw';
  return o;
}

/** Canonical key for matching boost rows to UI options. */
export function canonicalBoostOutcomeKey(outcome, item, isPoll = false) {
  return boostOutcomeStatsKey(outcome, item, isPoll);
}

export function netBoostStakeAmount(pred) {
  const stake = Number(pred?.totalStake ?? pred?.amount ?? pred?.originalStake ?? 0);
  return Number.isFinite(stake) && stake > 0 ? stake : 0;
}

/** True when a boost pick matches the resolved winning outcome (canonical keys). */
export function isBoostOutcomeWinner(pred, item, isPoll = false) {
  if (!pred || !item?.result) return false;
  const predKey = canonicalBoostOutcomeKey(pred.outcome, item, isPoll);
  const resultKey = canonicalBoostOutcomeKey(item.result, item, isPoll);
  return Boolean(predKey && resultKey && predKey === resultKey);
}

/** Build map optionKey -> merged boost prediction for table display. */
export function buildBoostStakeByOutcomeMap(boostPredictions, item, isPoll = false) {
  const map = new Map();
  for (const pred of boostPredictions || []) {
    const key = canonicalBoostOutcomeKey(pred.outcome, item, isPoll);
    if (!key) continue;
    const stake = netBoostStakeAmount(pred);
    const payout = Number(pred?.payout || 0);
    const claimed = Boolean(pred?.claimed);
    const detail = {
      id: pred._id ? String(pred._id) : '',
      claimed,
      payout,
    };
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        ...pred,
        totalStake: stake,
        amount: stake,
        payout,
        claimed,
        _mergedIds: detail.id ? [detail.id] : [],
        _mergedDetails: detail.id ? [detail] : [],
      });
      continue;
    }
    const combinedStake = stake + netBoostStakeAmount(existing);
    const combinedPayout = payout + Number(existing.payout || 0);
    const mergedIds = [
      ...(existing._mergedIds || (existing._id ? [String(existing._id)] : [])),
      ...(detail.id ? [detail.id] : []),
    ];
    const mergedDetails = [...(existing._mergedDetails || []), ...(detail.id ? [detail] : [])];
    map.set(key, {
      ...existing,
      ...pred,
      totalStake: combinedStake,
      amount: combinedStake,
      payout: combinedPayout,
      claimed: mergedDetails.length > 0 && mergedDetails.every((d) => d.claimed),
      _mergedIds: mergedIds,
      _mergedDetails: mergedDetails,
    });
  }
  return map;
}

/** Sum settled payout for all boost rows on the same outcome (handles duplicate DB rows). */
export function sumBoostOutcomePayout(boostPredictions, outcomeKey, item, isPoll = false) {
  let sum = 0;
  for (const pred of boostPredictions || []) {
    if (canonicalBoostOutcomeKey(pred.outcome, item, isPoll) !== outcomeKey) continue;
    sum += Number(pred?.payout || 0);
  }
  return sum;
}

/**
 * Orderbook CLOB: winning shares redeem at $1 each (not a parimutuel pool).
 * Returns estimated *profit* (payout − cost basis) if this side wins.
 * `positionPoolsByOption` is ignored — kept for call-site compatibility.
 */
export function estimateOrderbookPotentialWinFromPools({
  shares,
  totalInvested = 0,
  // eslint-disable-next-line no-unused-vars
  optionKey,
  // eslint-disable-next-line no-unused-vars
  side,
  // eslint-disable-next-line no-unused-vars
  positionPoolsByOption,
}) {
  const sh = Number(shares) || 0;
  if (!(sh > 0)) return 0;
  const invested = Math.max(0, Number(totalInvested) || 0);
  // Redeem value = shares × $1
  return Math.max(0, sh - invested);
}

/** Gross payout if this side wins (stake + profit ≈ shares × $1). */
export function grossOrderbookPotentialWin({ profit, invested, shares }) {
  const sh = Math.max(0, Number(shares) || 0);
  if (sh > 0) return sh;
  const net = Math.max(0, Number(profit) || 0);
  const inv = Math.max(0, Number(invested) || 0);
  if (inv > 0 && net > 0) return inv + net;
  return net;
}

/**
 * Preview profit for a proposed order.
 * Buy: if the side wins, each share pays $1 → profit = shares − cost (incl. fee).
 * Sell: cash proceeds now (not a resolution payout).
 * Pool args are ignored (legacy); CLOB is not parimutuel.
 */
export function estimateMarketOrderbookPotentialWin({
  direction,
  shares,
  price,
  feeRate = 0.1,
  // eslint-disable-next-line no-unused-vars
  optionKey,
  // eslint-disable-next-line no-unused-vars
  side,
  // eslint-disable-next-line no-unused-vars
  positionPoolsByOption,
}) {
  const sz = Number(shares) || 0;
  const px = Number(price);
  if (!(sz > 0) || !Number.isFinite(px) || px <= 0) return null;
  const fr = Number.isFinite(feeRate) && feeRate >= 0 ? feeRate : 0;

  if (direction === 'buy') {
    const cost = sz * px * (1 + fr);
    return Math.max(0, sz - cost);
  }

  const proceeds = sz * px * (1 - fr);
  return Math.max(0, proceeds);
}

function netStakeFromGross(gross, platformFeePct, jackpotFeePct) {
  const g = Number(gross) || 0;
  if (!(g > 0)) return 0;
  const platformFee = (g * (Number(platformFeePct) || 0)) / 100;
  const jpFee = (g * (Number(jackpotFeePct) || 0)) / 100;
  return Math.max(0, g - platformFee - jpFee);
}

/**
 * Boost winners split the full boostPool (net stakes + admin top-ups) by stake on the winning outcome.
 */
/** Free jackpot share if this pick wins (pool × your tickets / tickets on same outcome). */
export function estimateFreeJackpotPotentialWin({
  freeJackpotPoolUsdc = 0,
  userTickets = 1,
  outcomeTotalTickets = 0,
}) {
  const pool = Math.max(0, Number(freeJackpotPoolUsdc) || 0);
  const userT = Math.max(1, parseInt(userTickets, 10) || 1);
  const outcomeTotal = Math.max(userT, Number(outcomeTotalTickets) || 0);
  if (!(pool > 0)) return null;
  return pool * (userT / outcomeTotal);
}

export function estimateBoostPotentialWin({
  grossStakeUsdc,
  boostPoolUsdc = 0,
  existingNetStake = 0,
  winningOutcomeTotalStake = 0,
  platformFeePct = 10,
  jackpotFeePct = 0,
}) {
  const pool = Math.max(0, Number(boostPoolUsdc) || 0);
  const existing = Math.max(0, Number(existingNetStake) || 0);
  const netNew = netStakeFromGross(grossStakeUsdc, platformFeePct, jackpotFeePct);
  const userStake = existing + netNew;
  const outcomeTotal = Math.max(Number(winningOutcomeTotalStake) || 0, userStake);
  const projectedPool = pool + (Number(grossStakeUsdc) > 0 ? netNew : 0);
  if (userStake <= 0 || projectedPool <= 0 || outcomeTotal <= 0) return null;
  // Stake is already in the pool — winners split the full pool by net stake weight.
  return projectedPool * (userStake / outcomeTotal);
}
